import { Col, Form, Input, Row } from 'antd';
import { useTranslation } from 'react-i18next';
import TextArea from 'antd/lib/input/TextArea';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
 import { appInsights } from "../../Logging/appInsights";

import { actionCreators as GiftVoucherActions } from '../../store/GiftVoucher';
import { NO_CONTROL_CHARS, NO_WHITESPACE_OR_CONTROL_CHARS } from '../../helpers/validation';

export interface IRecipientInfoForm {
    prefilledEmail?: string;
    prefilledName?: string;
    prefilledMessage?: string;
    hidden: boolean;
}

const VoucherDetailInput: React.FunctionComponent<IRecipientInfoForm> = (props) => {
    const { t } = useTranslation();
    const { prefilledEmail, prefilledName, prefilledMessage, hidden } = props;
    const dispatch = useDispatch();
    const [form] = Form.useForm();

    const [emailAddress, setEmailAddress] = useState(prefilledEmail);
    const [fullName, setFullName] = useState(prefilledName);
    const [message, setMessage] = useState(prefilledMessage);

    const [emailValid, setValidEmail] = useState(prefilledEmail !== undefined);
    const [fullNameValid, setValidFullName] = useState(prefilledName !== undefined);

    const emailChanged = (email: string) => {
        setEmailAddress(email);
    };

    const fullNameChanged = (fullName: string) => {
        setFullName(fullName);
    };

    const messageChanged = (message: string) => {
        if (message.trim() !== '') {
            setMessage(message);
        }
    };

    useEffect(() => {
        dispatch(GiftVoucherActions.setRecipientDetailsValid(emailValid && fullNameValid));
    }, [emailValid, fullNameValid, dispatch]);

    useEffect(() => {
        let timer: any = null;
        timer = setInterval(() => {
            if (emailAddress) {
                form.validateFields(['emailAddress'])
                    .then((_values) => {
                        setValidEmail(true);
                        dispatch(GiftVoucherActions.recordToEmailAddress(emailAddress));
                    })
                    .catch((errorInfo) => {
                        setValidEmail(false);
                         appInsights?.trackException(
                             { error: new Error(`Error validating email : ${emailAddress}`), severityLevel: 2},
                             { exception: errorInfo, sourceFile: "voucher-detail-input.tsx", product: "booking" });
                    });
            }
            clearInterval(timer);
        }, 500);

        return () => {
            clearInterval(timer);
        };
    }, [dispatch, form, emailAddress]);

    useEffect(() => {
        let timer: any = null;
        timer = setInterval(() => {
            if (fullName) {
                form.validateFields(['fullName'])
                    .then((_values) => {
                        setValidFullName(true);
                        dispatch(GiftVoucherActions.recordToFullName(fullName));
                    })
                    .catch((errorInfo) => {
                        setValidFullName(false);
                         appInsights?.trackException(
                             { error: new Error(`Error validating fullname : ${fullName}`), severityLevel: 2},
                             { exception: errorInfo, sourceFile: "voucher-detail-input.tsx", product: "booking" });
                    });
            }
            clearInterval(timer);
        }, 500);

        return () => {
            clearInterval(timer);
        };
    }, [dispatch, form, fullName]);

    useEffect(() => {
        let timer: any = null;
        timer = setInterval(() => {
            if (message) {
                form.validateFields(['message'])
                    .then((_values) => {
                        dispatch(GiftVoucherActions.recordMessage(message));
                    })
                    .catch((errorInfo) => {
                         appInsights?.trackException(
                             { error: new Error(`Error validating message : ${message}`), severityLevel: 2},
                             { exception: errorInfo, sourceFile: "voucher-detail-input.tsx", product: "booking" });
                    });
            }
            clearInterval(timer);
        }, 500);

        return () => {
            clearInterval(timer);
        };
    }, [dispatch, form, message]);

    const fullNameValidationRules: Array<any> = [
        {
            required: true,
            max: 50,
            pattern: /^(?!\s*$).+/,
            message: 'Fullname required',
        },
        { pattern: NO_CONTROL_CHARS, message: t('playerInfo.controlCharError') },
    ];

    const emailAddressValidationRules: Array<any> = [
        { required: true, message: 'Email required' },
        { pattern: NO_WHITESPACE_OR_CONTROL_CHARS, message: t('playerInfo.emailAddress.whitespaceError') },
        { type: 'email', message: 'Incorrect email format' },
    ];

    return (
        <React.Fragment>
            <Form
                form={form}
                colon={false}
                initialValues={{
                    emailAddress: prefilledEmail,
                    fullName: prefilledName,
                    message: prefilledMessage,
                }}
                layout="horizontal"
                name="player-info-form"
                hidden={hidden}
            >
                <Row align="top" justify="start" gutter={[0, 24]}>
                    <Col span={24}>
                        <Form.Item
                            name="emailAddress"
                            className={`zl-form-item email show-label`}
                            hasFeedback={true}
                            rules={emailAddressValidationRules}
                        >
                            <Input
                                className="zl-input"
                                type="text"
                                inputMode="email"
                                onChange={(element) => emailChanged(element.currentTarget.value)}
                                placeholder={t('giftVoucherSection.details.emailLabel')}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={24}>
                        <Form.Item
                            name="fullName"
                            className={`zl-form-item show-label`}
                            hasFeedback={true}
                            rules={fullNameValidationRules}
                        >
                            <Input
                                className="zl-input"
                                onChange={(element) => fullNameChanged(element.currentTarget.value)}
                                placeholder={t('giftVoucherSection.details.nameLabel')}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={24}>
                        <Form.Item name="message" className={`zl-form-item show-label`}>
                            <TextArea
                                rows={4}
                                maxLength={250}
                                onChange={(element) => messageChanged(element.currentTarget.value)}
                                placeholder={t('giftVoucherSection.details.messageLabel')}
                            ></TextArea>
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
        </React.Fragment>
    );
};

export default VoucherDetailInput;
